import {
  BundleState,
  BundleStatus,
  useSupportBundleProgress,
} from 'core/context/supportBundleProgressContext'
import Tooltip from 'core/elements/tooltip'
import Text from 'core/elements/Text'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import Button from 'core/elements/button'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import React from 'react'
import { cancelSupportBundle } from 'app/plugins/openstack/components/infrastructure/actions'
import clsx from 'clsx'

export const BUNDLE_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  PARTIAL_SUCCESS: 'partial_success',
} as const

interface StatusConfig {
  icon: string
  iconClassName?: string
  badgeVariant: 'success' | 'error' | 'primary' | 'warning'
  badgeText: string
  spin?: boolean
  tooltipText?: string
}

const STATUS_CONFIG: Record<BundleStatus | 'default', StatusConfig> = {
  [BUNDLE_STATUS.PENDING]: {
    icon: 'arrows-rotate',
    badgeVariant: 'primary',
    badgeText: 'Ongoing',
    spin: true,
  },
  [BUNDLE_STATUS.IN_PROGRESS]: {
    icon: 'arrows-rotate',
    badgeVariant: 'primary',
    badgeText: 'Ongoing',
    spin: true,
  },
  [BUNDLE_STATUS.COMPLETED]: {
    icon: 'check',
    iconClassName: 'completedIcon',
    badgeVariant: 'success',
    badgeText: 'Completed',
  },
  [BUNDLE_STATUS.FAILED]: {
    icon: 'xmark',
    iconClassName: 'failedIcon',
    badgeVariant: 'error',
    badgeText: 'Failed',
  },
  [BUNDLE_STATUS.CANCELLED]: {
    icon: 'ban',
    badgeVariant: 'error',
    badgeText: 'Cancelled',
  },
  [BUNDLE_STATUS.PARTIAL_SUCCESS]: {
    icon: 'triangle-exclamation',
    iconClassName: 'partialSuccessIcon',
    badgeVariant: 'warning',
    badgeText: 'Partial Success',
    tooltipText:
      'Some hosts failed to generate support bundle. If you download the bundle, some hosts may not be included.',
  },
  default: {
    icon: 'arrows-rotate',
    badgeVariant: 'primary',
    badgeText: 'Ongoing',
    spin: true,
  },
}

interface BundleRowProps {
  index: number
  bundle: BundleState
  bundlesCount: number
  onDownload: (bundleId: string) => void
  onClose: (bundleId: string) => void
}

const BundleRow: React.FC<BundleRowProps> = ({
  index,
  bundle,
  bundlesCount,
  onDownload,
  onClose,
}) => {
  const classes = useStyles()
  const status = bundle.overallStatus
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.default
  const { updateBundleStatus, removeBundle } = useSupportBundleProgress()

  const [openCancelDialog, setOpenCancelDialog] = React.useState(false)

  const isCompleted = status === BUNDLE_STATUS.COMPLETED || status === BUNDLE_STATUS.PARTIAL_SUCCESS

  const canClose =
    status === BUNDLE_STATUS.COMPLETED ||
    status === BUNDLE_STATUS.FAILED ||
    status === BUNDLE_STATUS.CANCELLED ||
    status === BUNDLE_STATUS.PARTIAL_SUCCESS

  const canCancel = status === BUNDLE_STATUS.PENDING || status === BUNDLE_STATUS.IN_PROGRESS

  const handleDownload = () => {
    if (!bundle.downloading) {
      onDownload(bundle.bundleId)
    }
  }

  // Cancel bundle
  const cancelBundle = async (bundleId: string) => {
    try {
      await cancelSupportBundle({ bundleId })
      updateBundleStatus(bundleId, {
        overallStatus: BUNDLE_STATUS.CANCELLED as BundleStatus,
      })
      removeBundle(bundleId)
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to cancel bundle')

      updateBundleStatus(bundleId, {
        error: errorMessage,
      })
    }
  }
  // Show error message from the first host in the bundle
  const statusTooltip =
    (status === BUNDLE_STATUS.FAILED &&
      bundle?.hostStatuses?.[Object.keys(bundle?.hostStatuses || {})[0]]?.error) ||
    config.tooltipText ||
    ''

  return (
    <div className={clsx(classes.bundleRow, bundlesCount > 1 && classes.bundleRowWithBorder)}>
      <Tooltip message={bundle.bundleId}>
        <Text variant="body2" className={classes.bundleId}>
          Bundle - {index + 1}
        </Text>
      </Tooltip>

      <div className={classes.actionsCell}>
        {bundle.error ? (
          <Tooltip message={bundle.error}>
            <BadgeWithIcon
              text="Error"
              variant="error"
              icon="circle-exclamation"
              data-testid="bundle-error-badge"
            />
          </Tooltip>
        ) : (
          <Tooltip message={statusTooltip}>
            <BadgeWithIcon
              text={config.badgeText || ''}
              variant={config.badgeVariant || 'primary'}
              icon={config.icon || ''}
              spin={config.spin || false}
              data-testid={`${config.icon}-badge`}
              className={classes[config.iconClassName]}
            />
          </Tooltip>
        )}

        {isCompleted && (
          <Tooltip
            message={
              bundle.downloadError ||
              (bundle.downloading ? 'Bundle is being downloaded' : 'Download bundle')
            }
          >
            <FontAwesomeIcon
              size="md"
              solid
              spin={bundle.downloading}
              onClick={handleDownload}
              className={bundle.downloadError ? classes.downloadErrorIcon : undefined}
              data-testid={`download-bundle-button-${bundle.bundleId}`}
            >
              {bundle.downloading ? 'circle-notch' : 'download'}
            </FontAwesomeIcon>
          </Tooltip>
        )}

        <ConfirmationDialog
          open={openCancelDialog}
          title="Cancel Bundle Generation"
          text="This action cannot be undone. Are you sure you want to cancel the bundle generation?"
          cancelText="Cancel"
          confirmText="Confirm"
          onCancel={() => setOpenCancelDialog(false)}
          onConfirm={() => {
            setOpenCancelDialog(false)
            cancelBundle(bundle.bundleId)
          }}
        />

        {canCancel && (
          <Tooltip message="Bundle is being generated. You can cancel the generation process.">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setOpenCancelDialog(true)}
              data-testid={`cancel-bundle-button-${bundle.bundleId}`}
            >
              Cancel
            </Button>
          </Tooltip>
        )}

        {canClose && (
          <Tooltip message="Remove bundle from list">
            <FontAwesomeIcon
              size="md"
              solid
              onClick={() => onClose(bundle.bundleId)}
              data-testid={`close-bundle-button-${bundle.bundleId}`}
            >
              close
            </FontAwesomeIcon>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
export default BundleRow

interface StyleProps {
  bundlesCount: number
}

const useStyles = makeStyles<Theme>((theme) => ({
  bundleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 'none',
    padding: theme.spacing(1, 1.5),
  },
  bundleRowWithBorder: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  bundleId: {
    fontWeight: 500,
    cursor: 'help',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  completedIcon: {
    color: theme.palette.success.main,
    cursor: 'pointer',
  },
  failedIcon: {
    color: theme.palette.error.main,
    cursor: 'pointer',
  },
  partialSuccessIcon: {
    color: theme.palette.warning.main,
    cursor: 'pointer',
  },
  downloadErrorIcon: {
    color: theme.palette.error.main,
    cursor: 'pointer',
  },
}))
